/**
 * Queue State Management Hook
 * Manages the parsing review queue state including items, navigation, and corrections
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../../utils/api';
import { useReviewQueueEvents } from '../../../contexts/SSEContext';

/**
 * Initial state for queue
 */
const initialState = {
  items: [],
  currentIndex: 0,
  currentItem: null,
  total: 0,
  loading: false,
  error: null,
  stats: null
};

/**
 * Initial state for filters
 */
const initialFilters = {
  status: 'active',
  priority: '',
  documentType: '',
  assignedTo: ''
};

/**
 * Initial state for correction
 */
const initialCorrection = {
  documentType: 'flight',
  parsedData: {},
  originalData: {},
  smartInputValue: '',
  isDirty: false,
  validationErrors: [],
  notes: ''
};

/**
 * Hook for managing queue state
 */
export const useQueueState = (userId) => {
  // Queue state
  const [queue, setQueue] = useState(initialState);
  const [filters, setFilters] = useState(initialFilters);
  const [correction, setCorrection] = useState(initialCorrection);
  const [pagination, setPagination] = useState({ offset: 0, limit: 50 });

  // Selection state for bulk operations
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Refs for tracking
  const loadingRef = useRef(false);
  const abortControllerRef = useRef(null);

  /**
   * Load queue items from API
   */
  const loadQueue = useCallback(async (forceRefresh = false) => {
    if (loadingRef.current && !forceRefresh) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    loadingRef.current = true;
    setQueue(prev => ({ ...prev, loading: true, error: null }));

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset,
        sort_by: 'created_at',
        sort_order: 'desc'
      });

      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.documentType) params.append('document_type', filters.documentType);
      if (filters.assignedTo) params.append('assigned_to', filters.assignedTo);

      const response = await axios.get(
        `${apiBase}/admin/parsing-qa/queue?${params.toString()}`,
        { headers, signal: abortControllerRef.current.signal }
      );

      const items = response.data.items || [];
      const total = response.data.total || 0;

      setQueue(prev => {
        const newIndex = prev.currentIndex >= items.length ? 0 : prev.currentIndex;
        // Preserve currentItem if it exists and is still in the list (or was loaded separately)
        // This prevents overwriting a fully-loaded/assigned item with a stale queue list entry
        const currentItemStillValid = prev.currentItem && (
          items.some(i => i.id === prev.currentItem.id) ||
          // Item may have transitioned out of the filtered status (e.g., pending → in_progress)
          prev.currentItem.status === 'in_progress'
        );
        return {
          ...prev,
          items,
          total,
          loading: false,
          currentIndex: newIndex,
          currentItem: currentItemStillValid
            ? prev.currentItem
            : (items.length > 0 ? items[Math.min(newIndex, items.length - 1)] : null)
        };
      });

      // Load full item details if we have items (don't auto-assign on initial load)
      if (items.length > 0) {
        const currentIdx = Math.min(queue.currentIndex, items.length - 1);
        await loadItemDetails(items[currentIdx].id, false);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error loading queue:', err);
        setQueue(prev => ({
          ...prev,
          loading: false,
          error: err.response?.data?.detail || err.message
        }));
      }
    } finally {
      loadingRef.current = false;
    }
  }, [filters, pagination, queue.currentIndex]);

  /**
   * Load queue statistics
   */
  const loadStats = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/parsing-qa/queue/stats`, { headers });
      setQueue(prev => ({ ...prev, stats: response.data.stats }));
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, []);

  /**
   * Load full details for a specific item and auto-assign it
   */
  const loadItemDetails = useCallback(async (itemId, autoAssign = true) => {
    if (!itemId) return;

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/parsing-qa/queue/${itemId}`, { headers });

      const item = response.data.item;
      if (item) {
        setQueue(prev => ({ ...prev, currentItem: item }));

        // Initialize correction state from item
        // Use corrected_data if available (for approved items), otherwise ai_parsed_data
        const displayData = item.corrected_data || item.ai_parsed_data || {};
        setCorrection({
          // An item with no type is not a flight; show it as a generic booking.
          documentType: item.document_type || 'occurrence',
          parsedData: { ...displayData },
          originalData: { ...(item.ai_parsed_data || {}) },
          smartInputValue: '',
          isDirty: false,
          validationErrors: item.validation_errors || [],
          notes: ''
        });

        // Auto-assign if item is not already in_progress and autoAssign is true
        // This handles: unassigned pending items, and items that need status transition
        const needsAssignment = item.status === 'pending' || (item.status !== 'in_progress' && item.status !== 'approved' && item.status !== 'rejected');
        if (autoAssign && needsAssignment) {
          try {
            const assignResponse = await axios.post(
              `${apiBase}/admin/parsing-qa/queue/${itemId}/assign`,
              {},
              { headers }
            );
            const assignedItem = assignResponse.data.item;
            if (assignedItem) {
              setQueue(prev => ({
                ...prev,
                currentItem: assignedItem,
                items: prev.items.map(i => i.id === assignedItem.id ? assignedItem : i)
              }));
              console.log(`Auto-assigned item ${itemId} to current user`);
            }
          } catch (assignErr) {
            if (assignErr.response?.status === 409) {
              // Item is assigned to another user - show who has it
              console.warn('Item already assigned:', assignErr.response.data.detail);
            } else {
              console.warn('Auto-assign failed:', assignErr.response?.data?.detail || assignErr.message);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error loading item details:', err);
    }
  }, []);

  /**
   * Navigate to next item
   */
  const nextItem = useCallback(async () => {
    if (queue.items.length === 0) return;

    const newIndex = Math.min(queue.currentIndex + 1, queue.items.length - 1);
    if (newIndex !== queue.currentIndex) {
      setQueue(prev => ({ ...prev, currentIndex: newIndex }));
      await loadItemDetails(queue.items[newIndex].id);
    }
  }, [queue.items, queue.currentIndex, loadItemDetails]);

  /**
   * Navigate to previous item
   */
  const prevItem = useCallback(async () => {
    if (queue.items.length === 0) return;

    const newIndex = Math.max(queue.currentIndex - 1, 0);
    if (newIndex !== queue.currentIndex) {
      setQueue(prev => ({ ...prev, currentIndex: newIndex }));
      await loadItemDetails(queue.items[newIndex].id);
    }
  }, [queue.items, queue.currentIndex, loadItemDetails]);

  /**
   * Navigate to first item
   */
  const firstItem = useCallback(async () => {
    if (queue.items.length === 0) return;

    if (queue.currentIndex !== 0) {
      setQueue(prev => ({ ...prev, currentIndex: 0 }));
      await loadItemDetails(queue.items[0].id);
    }
  }, [queue.items, queue.currentIndex, loadItemDetails]);

  /**
   * Navigate to last item
   */
  const lastItem = useCallback(async () => {
    if (queue.items.length === 0) return;

    const lastIndex = queue.items.length - 1;
    if (queue.currentIndex !== lastIndex) {
      setQueue(prev => ({ ...prev, currentIndex: lastIndex }));
      await loadItemDetails(queue.items[lastIndex].id);
    }
  }, [queue.items, queue.currentIndex, loadItemDetails]);

  /**
   * Navigate to specific item by ID
   */
  const goToItem = useCallback(async (itemId) => {
    const index = queue.items.findIndex(item => item.id === itemId);
    if (index >= 0) {
      setQueue(prev => ({ ...prev, currentIndex: index }));
      await loadItemDetails(itemId);
    } else {
      // Item not in current list, load it directly
      await loadItemDetails(itemId);
    }
  }, [queue.items, loadItemDetails]);

  /**
   * Assign current item to user
   */
  const assignItem = useCallback(async () => {
    if (!queue.currentItem) return { success: false, error: 'No item selected' };

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.post(
        `${apiBase}/admin/parsing-qa/queue/${queue.currentItem.id}/assign`,
        {},
        { headers }
      );

      const updatedItem = response.data.item;
      setQueue(prev => ({
        ...prev,
        currentItem: updatedItem,
        items: prev.items.map(item =>
          item.id === updatedItem.id ? updatedItem : item
        )
      }));

      return { success: true };
    } catch (err) {
      console.error('Error assigning item:', err);
      return { success: false, error: err.response?.data?.detail || err.message };
    }
  }, [queue.currentItem]);

  /**
   * Unassign the current item (release back to queue)
   */
  const unassignItem = useCallback(async () => {
    if (!queue.currentItem) return { success: false, error: 'No item selected' };

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(
        `${apiBase}/admin/parsing-qa/queue/${queue.currentItem.id}/unassign`,
        {},
        { headers }
      );

      // Update local state - clear assignment fields
      setQueue(prev => {
        const updatedItem = {
          ...prev.currentItem,
          status: 'pending',
          assigned_to_user_id: null,
          assigned_to_user_email: null,
          assigned_at: null,
          last_activity_at: null,
        };
        return {
          ...prev,
          currentItem: updatedItem,
          items: prev.items.map(i =>
            i.id === updatedItem.id ? updatedItem : i
          )
        };
      });

      return { success: true };
    } catch (err) {
      console.error('Error unassigning item:', err);
      return { success: false, error: err.response?.data?.detail || err.message };
    }
  }, [queue.currentItem]);

  /**
   * Complete review (approve or reject)
   */
  const completeReview = useCallback(async (status, options = {}) => {
    if (!queue.currentItem) return { success: false, error: 'No item selected' };

    const { correctedData = correction.parsedData, notes = correction.notes } = options;

    // Ensure document_type is included in corrected data for component creation routing
    const dataToSend = correctedData ? {
      ...correctedData,
      document_type: correctedData.document_type || correction.documentType || queue.currentItem.document_type
    } : null;

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Calculate corrections
      const corrections = [];
      if (correctedData && status === 'approved') {
        const original = queue.currentItem.ai_parsed_data || {};
        for (const [key, value] of Object.entries(correctedData)) {
          if (JSON.stringify(original[key]) !== JSON.stringify(value)) {
            corrections.push({
              field: key,
              old: original[key],
              new: value
            });
          }
        }
      }

      await axios.post(
        `${apiBase}/admin/parsing-qa/queue/${queue.currentItem.id}/complete`,
        {
          status,
          corrected_data: status === 'approved' ? dataToSend : null,
          corrections_applied: corrections,
          review_notes: notes
        },
        { headers }
      );

      // Remove item from list and move to next
      const newItems = queue.items.filter(item => item.id !== queue.currentItem.id);
      const newIndex = Math.min(queue.currentIndex, newItems.length - 1);

      setQueue(prev => ({
        ...prev,
        items: newItems,
        total: prev.total - 1,
        currentIndex: newIndex >= 0 ? newIndex : 0,
        currentItem: newItems.length > 0 ? newItems[Math.max(0, newIndex)] : null
      }));

      // Reset correction state
      if (newItems.length > 0 && newIndex >= 0) {
        await loadItemDetails(newItems[newIndex].id);
      } else {
        setCorrection(initialCorrection);
      }

      // Refresh stats
      loadStats();

      return { success: true };
    } catch (err) {
      console.error('Error completing review:', err);
      return { success: false, error: err.response?.data?.detail || err.message };
    }
  }, [queue, correction, loadItemDetails, loadStats]);

  /**
   * Update correction data
   */
  const updateCorrectionData = useCallback((updates) => {
    setCorrection(prev => ({
      ...prev,
      parsedData: { ...prev.parsedData, ...updates },
      isDirty: true
    }));
  }, []);

  /**
   * Update a specific field in correction data
   */
  const updateField = useCallback((field, value) => {
    setCorrection(prev => ({
      ...prev,
      parsedData: { ...prev.parsedData, [field]: value },
      isDirty: true
    }));
  }, []);

  /**
   * Update leg data
   */
  const updateLeg = useCallback((legIndex, legData) => {
    setCorrection(prev => {
      const legs = [...(prev.parsedData.legs || [])];
      legs[legIndex] = { ...legs[legIndex], ...legData };
      return {
        ...prev,
        parsedData: { ...prev.parsedData, legs },
        isDirty: true
      };
    });
  }, []);

  /**
   * Add a new leg
   */
  const addLeg = useCallback(() => {
    setCorrection(prev => {
      const legs = [...(prev.parsedData.legs || [])];
      legs.push({ leg_number: legs.length + 1 });
      return {
        ...prev,
        parsedData: { ...prev.parsedData, legs },
        isDirty: true
      };
    });
  }, []);

  /**
   * Remove a leg
   */
  const removeLeg = useCallback((legIndex) => {
    setCorrection(prev => {
      const legs = [...(prev.parsedData.legs || [])];
      legs.splice(legIndex, 1);
      // Renumber legs
      legs.forEach((leg, idx) => {
        leg.leg_number = idx + 1;
      });
      return {
        ...prev,
        parsedData: { ...prev.parsedData, legs },
        isDirty: true
      };
    });
  }, []);

  /**
   * Change document type
   */
  const changeDocumentType = useCallback((newType) => {
    setCorrection(prev => ({
      ...prev,
      documentType: newType,
      parsedData: { ...prev.parsedData, document_type: newType },
      isDirty: true
    }));
  }, []);

  /**
   * Apply parsed data from smart input
   */
  const applyParsedData = useCallback((parsedData, merge = true) => {
    setCorrection(prev => ({
      ...prev,
      parsedData: merge ? { ...prev.parsedData, ...parsedData } : parsedData,
      isDirty: true
    }));
  }, []);

  /**
   * Set smart input value
   */
  const setSmartInputValue = useCallback((value) => {
    setCorrection(prev => ({ ...prev, smartInputValue: value }));
  }, []);

  /**
   * Set review notes
   */
  const setNotes = useCallback((notes) => {
    setCorrection(prev => ({ ...prev, notes }));
  }, []);

  /**
   * Reset correction to original data
   */
  const resetCorrection = useCallback(() => {
    if (queue.currentItem) {
      setCorrection({
        documentType: queue.currentItem.document_type || 'occurrence',
        parsedData: { ...(queue.currentItem.ai_parsed_data || {}) },
        originalData: { ...(queue.currentItem.ai_parsed_data || {}) },
        smartInputValue: '',
        isDirty: false,
        validationErrors: queue.currentItem.validation_errors || [],
        notes: ''
      });
    }
  }, [queue.currentItem]);

  // =========================================================================
  // Selection and Bulk Delete
  // =========================================================================

  /**
   * Toggle selection of a single item
   */
  const toggleItemSelection = useCallback((itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  /**
   * Select all items in current view
   */
  const selectAll = useCallback(() => {
    const allIds = queue.items.map(item => item.id);
    setSelectedItems(new Set(allIds));
  }, [queue.items]);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelectedItems(new Set());
  }, []);

  /**
   * Check if an item is selected
   */
  const isItemSelected = useCallback((itemId) => {
    return selectedItems.has(itemId);
  }, [selectedItems]);

  /**
   * Delete selected items
   */
  const deleteSelectedItems = useCallback(async () => {
    if (selectedItems.size === 0) {
      return { success: false, error: 'No items selected' };
    }

    // Capture selected IDs before clearing
    const idsToDelete = Array.from(selectedItems);
    const deletedIds = new Set(idsToDelete);

    setIsDeleting(true);

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const response = await axios.post(
        `${apiBase}/admin/parsing-qa/queue/bulk-delete`,
        { item_ids: idsToDelete },
        { headers }
      );

      const deletedCount = response.data.deleted_count || 0;

      // Clear selection immediately
      setSelectedItems(new Set());

      // Update queue state and track new current item for loading
      let nextItemToLoad = null;
      setQueue(prev => {
        const newItems = prev.items.filter(item => !deletedIds.has(item.id));
        let newCurrentIndex = 0;
        let newCurrentItem = null;

        if (newItems.length > 0) {
          newCurrentIndex = Math.min(prev.currentIndex, newItems.length - 1);
          newCurrentItem = newItems[newCurrentIndex];
          nextItemToLoad = newCurrentItem;
        }

        return {
          ...prev,
          items: newItems,
          total: prev.total - deletedCount,
          currentIndex: newCurrentIndex,
          currentItem: newCurrentItem
        };
      });

      // Reset correction state - will be populated when queue state updates
      setCorrection(initialCorrection);

      // Refresh stats
      loadStats();

      return { success: true, deletedCount };
    } catch (err) {
      console.error('Error deleting items:', err);
      return { success: false, error: err.response?.data?.detail || err.message };
    } finally {
      setIsDeleting(false);
    }
  }, [selectedItems, loadStats]);

  /**
   * Delete single item
   */
  const deleteItem = useCallback(async (itemId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      await axios.delete(
        `${apiBase}/admin/parsing-qa/queue/${itemId}`,
        { headers }
      );

      // Remove from selection if selected
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });

      // Update queue state
      setQueue(prev => {
        const newItems = prev.items.filter(item => item.id !== itemId);
        let newCurrentIndex = 0;
        let newCurrentItem = null;

        if (newItems.length > 0) {
          newCurrentIndex = Math.min(prev.currentIndex, newItems.length - 1);
          newCurrentItem = newItems[newCurrentIndex];
        }

        return {
          ...prev,
          items: newItems,
          total: prev.total - 1,
          currentIndex: newCurrentIndex,
          currentItem: newCurrentItem
        };
      });

      // Reset correction state - will be populated when queue state updates
      setCorrection(initialCorrection);

      // Refresh stats
      loadStats();

      return { success: true };
    } catch (err) {
      console.error('Error deleting item:', err);
      return { success: false, error: err.response?.data?.detail || err.message };
    }
  }, [loadStats]);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  }, []);

  // Load queue on mount and filter changes
  useEffect(() => {
    loadQueue();
    loadStats();
  }, [loadQueue, loadStats]);

  // =========================================================================
  // SSE Real-time Updates
  // =========================================================================

  /**
   * Handle real-time SSE events from other users
   */
  const handleSSEEvent = useCallback((event) => {
    console.log('📡 SSE Review Queue Event:', event);

    switch (event.type) {
      case 'deleted':
        // Single item deleted by another user
        setQueue(prev => {
          const deletedId = event.data?.item?.id;
          if (!deletedId) return prev;

          const newItems = prev.items.filter(item => item.id !== deletedId);
          let newCurrentIndex = prev.currentIndex;
          let newCurrentItem = prev.currentItem;

          if (prev.currentItem?.id === deletedId) {
            // Current item was deleted
            newCurrentIndex = Math.min(prev.currentIndex, newItems.length - 1);
            newCurrentItem = newItems.length > 0 ? newItems[Math.max(0, newCurrentIndex)] : null;
          }

          return {
            ...prev,
            items: newItems,
            total: prev.total - 1,
            currentIndex: newCurrentIndex,
            currentItem: newCurrentItem
          };
        });
        // Remove from selection if selected
        setSelectedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(event.data?.item?.id);
          return newSet;
        });
        loadStats();
        break;

      case 'bulk_deleted':
        // Multiple items deleted by another user
        setQueue(prev => {
          const deletedIds = new Set(event.data?.deleted_ids || []);
          if (deletedIds.size === 0) return prev;

          const newItems = prev.items.filter(item => !deletedIds.has(item.id));
          let newCurrentIndex = prev.currentIndex;
          let newCurrentItem = prev.currentItem;

          if (prev.currentItem && deletedIds.has(prev.currentItem.id)) {
            // Current item was deleted
            newCurrentIndex = Math.min(prev.currentIndex, newItems.length - 1);
            newCurrentItem = newItems.length > 0 ? newItems[Math.max(0, newCurrentIndex)] : null;
          }

          return {
            ...prev,
            items: newItems,
            total: prev.total - deletedIds.size,
            currentIndex: newCurrentIndex,
            currentItem: newCurrentItem
          };
        });
        // Remove from selection
        setSelectedItems(prev => {
          const deletedIds = new Set(event.data?.deleted_ids || []);
          const newSet = new Set([...prev].filter(id => !deletedIds.has(id)));
          return newSet;
        });
        loadStats();
        break;

      case 'assigned':
        // Item assigned by another user
        setQueue(prev => {
          const itemData = event.data?.item;
          if (!itemData?.id) return prev;

          const updatedItems = prev.items.map(item =>
            item.id === itemData.id
              ? { ...item, ...itemData }
              : item
          );

          return {
            ...prev,
            items: updatedItems,
            currentItem: prev.currentItem?.id === itemData.id
              ? { ...prev.currentItem, ...itemData }
              : prev.currentItem
          };
        });
        break;

      case 'completed':
        // Item completed (approved/rejected) by another user - remove from queue
        setQueue(prev => {
          const completedId = event.data?.item?.id;
          if (!completedId) return prev;

          const newItems = prev.items.filter(item => item.id !== completedId);
          let newCurrentIndex = prev.currentIndex;
          let newCurrentItem = prev.currentItem;

          if (prev.currentItem?.id === completedId) {
            // Current item was completed
            newCurrentIndex = Math.min(prev.currentIndex, newItems.length - 1);
            newCurrentItem = newItems.length > 0 ? newItems[Math.max(0, newCurrentIndex)] : null;
          }

          return {
            ...prev,
            items: newItems,
            total: prev.total - 1,
            currentIndex: newCurrentIndex,
            currentItem: newCurrentItem
          };
        });
        setSelectedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(event.data?.item?.id);
          return newSet;
        });
        loadStats();
        break;

      case 'created':
        // New item created - refresh queue to include it
        loadQueue(true);
        loadStats();
        break;

      case 'updated':
        // Item updated by another user
        setQueue(prev => {
          const itemData = event.data?.item;
          if (!itemData?.id) return prev;

          const updatedItems = prev.items.map(item =>
            item.id === itemData.id
              ? { ...item, ...itemData }
              : item
          );

          return {
            ...prev,
            items: updatedItems,
            currentItem: prev.currentItem?.id === itemData.id
              ? { ...prev.currentItem, ...itemData }
              : prev.currentItem
          };
        });
        break;

      default:
        console.log('Unknown SSE event type:', event.type);
    }
  }, [loadQueue, loadStats]);

  // Subscribe to SSE events for real-time updates
  useReviewQueueEvents(handleSSEEvent, [handleSSEEvent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // State
    queue,
    filters,
    correction,
    pagination,
    selectedItems,
    isDeleting,

    // Navigation
    nextItem,
    prevItem,
    firstItem,
    lastItem,
    goToItem,

    // Actions
    loadQueue,
    loadStats,
    loadItemDetails,
    assignItem,
    unassignItem,
    completeReview,

    // Selection and delete
    toggleItemSelection,
    selectAll,
    clearSelection,
    isItemSelected,
    deleteSelectedItems,
    deleteItem,

    // Correction management
    updateCorrectionData,
    updateField,
    updateLeg,
    addLeg,
    removeLeg,
    changeDocumentType,
    applyParsedData,
    setSmartInputValue,
    setNotes,
    resetCorrection,

    // Filter management
    updateFilters,
    setPagination
  };
};

export default useQueueState;
